import {
  faTrashAlt,
  faGripLines,
  faUnlock,
  faLock,
} from '@fortawesome/free-solid-svg-icons'
import { CueType } from '@rundown-studio/types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, memo } from 'react'
import PropTypes from 'prop-types'
import ModalWrapper from '../modal/ModalWrapper.tsx'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../interactives/Tooltip.jsx'
import {
  filteredAndSortedCuesAtom,
  isCueSelectedAtom,
  rundownAtom,
  selectedCueIdsAtom,
  toggleSelectedCuesAtom,
} from '../../store/rundown.store'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { cn } from '../../utils/index.js'
import { useCueDragHandle } from '../../features/cues-list'

function CueItemSettings({
  cue,
  readonly,
  onUpdateCue,
  onDeleteCue,
  parentId,
  SettingsMenu,
}) {
  const [deletingGroup, setDeletingGroup] = useState(false)
  const filteredAndSortedCues = useAtomValue(filteredAndSortedCuesAtom)
  const rundown = useAtomValue(rundownAtom)
  const getIsCueSelected = useAtomValue(isCueSelectedAtom)
  const [selectedCues, setSelectedCues] = useAtom(selectedCueIdsAtom)
  const toggleSelectedCue = useSetAtom(toggleSelectedCuesAtom)
  const isCueSelected = getIsCueSelected(cue.id)
  const isParentCueSelected = getIsCueSelected(parentId)

  /**
   * Handles the selection of cues in the rundown.
   *
   * - If the shift key is held during selection, selects a range of cues from the
   *   last selected cue to the current one. If any group cues are selected in this
   *   range, their child cues are removed from the selection.
   * - If selecting a group cue directly, removes any selected children cues of that
   *   group and selects the group cue itself.
   * - Otherwise, toggles the selected state of the individual cue.
   *
   * @param {Object} event - The selection event.
   */
  function onSelectCue(event) {
    // Check if shift key is held to perform range selection
    if (event.nativeEvent.shiftKey) {
      const sortedCueIds = filteredAndSortedCues.map((c) => c.id)
      const lastSelectedIndex = sortedCueIds.indexOf(
        selectedCues[selectedCues.length - 1],
      )
      const currentIndex = sortedCueIds.indexOf(cue.id)

      // Determine the start and end indices for the range selection
      const [start, end] = [lastSelectedIndex, currentIndex].sort(
        (a, b) => a - b,
      )
      const newSelectedIds = sortedCueIds.slice(start, end + 1)

      // Combine current selections with the new range, removing duplicates
      const updatedSelectedCues = Array.from(
        new Set([...selectedCues, ...newSelectedIds]),
      )

      // If any group cues are selected, remove their child cues
      const groupHeaders =
        rundown.cues?.filter(
          (c) => c.children && updatedSelectedCues.includes(c.id),
        ) ?? []
      const selectedChildren =
        groupHeaders?.flatMap((g) => g.children.map((c) => c.id)) ?? []
      const selectedWithoutChildren =
        updatedSelectedCues?.filter((id) => !selectedChildren?.includes(id)) ??
        []

      setSelectedCues(selectedWithoutChildren)
    } else {
      // Handle individual cue selection or group cue selection
      if (event.target.checked && cue.type === CueType.GROUP) {
        // Remove any selected children cues of this group and select the group cue
        const childrenCues = rundown.cues
          .find((c) => c.id === cue.id)
          .children?.map((c) => c.id)
        const selectedWithoutChildren = selectedCues.filter(
          (id) => !childrenCues.includes(id),
        )
        setSelectedCues([...selectedWithoutChildren, cue.id])
      } else {
        // Toggle the selected state of the individual cue
        toggleSelectedCue(cue.id)
      }
    }
  }

  const { setActivatorNodeRef, listeners } = useCueDragHandle(cue.id)

  if (readonly) return null

  return (
    <div
      className={cn(
        'sticky top-27 flex flex-col group-hover:backdrop-blur-sm min-h-14 rounded-sm',
        (isParentCueSelected || isCueSelected) && 'bg-blue-700/90',
        selectedCues.length > 0 ? 'h-full' : '', // Force full height when any items are selected to ensure background stretches to full height
      )}
    >
      {/* Drag Handle */}
      {cue.settings?.preventEdits || selectedCues.length > 0 ? (
        <span className="h-6"></span>
      ) : (
        <button
          ref={setActivatorNodeRef}
          {...listeners}
          className="flex-none w-7 h-7 opacity-0 group-hover:opacity-70 hover:opacity-100! cursor-grab! rounded-sm focus:outline-hidden focus:ring-3"
        >
          <FontAwesomeIcon icon={faGripLines} />
        </button>
      )}

      {SettingsMenu}

      {/* Lock Button OR Collapse button */}
      {cue.settings?.preventEdits ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="flex-none w-7 h-7 rounded-sm group-hover:opacity-70 hover:opacity-100! transition-opacity backdrop-blur-sm text-xs focus:outline-hidden focus:ring-3 group/lock opacity-70 text-yellow-500"
              onClick={() => onUpdateCue({ 'settings.preventEdits': false })}
            >
              <span className="hidden group-hover/lock:inline">
                <FontAwesomeIcon icon={faUnlock} size="sm" />
              </span>
              <span className="group-hover/lock:hidden">
                <FontAwesomeIcon icon={faLock} size="sm" />
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent>Allow cue editing</TooltipContent>
        </Tooltip>
      ) : (
        !isParentCueSelected && (
          <Tooltip>
            <TooltipTrigger>
              <div className="flex flex-col justify-center align-middle w-full h-7 rounded-sm">
                <label
                  htmlFor={`select-${cue.id}`}
                  className={cn('mx-auto p-1.5 items-center')}
                >
                  <input
                    type="checkbox"
                    checked={isCueSelected}
                    onChange={onSelectCue}
                    id={`select-${cue.id}`}
                    className={[
                      'mx-auto group-hover:opacity-70 hover:opacity-100! transition-opacity text-xs focus:outline-hidden focus:ring-3',
                      isCueSelected ? 'opacity-70' : 'opacity-0',
                    ].join(' ')}
                  />
                </label>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              {isCueSelected ? 'Deselect' : 'Select'}{' '}
              {cue.type === CueType.GROUP ? 'group' : 'cue'}
            </TooltipContent>
          </Tooltip>
        )
      )}
      {deletingGroup && (
        <ModalWrapper
          open={[deletingGroup, setDeletingGroup]}
          title="Delete Group"
          buttons={[
            {
              text: 'Cancel',
              onClick: () => setDeletingGroup(false),
            },
            {
              text: 'Delete Group',
              icon: faTrashAlt,
              onClick: async () => {
                setDeletingGroup(false)
                onDeleteCue()
              },
            },
          ]}
        >
          <p>Deleting this group will permanently remove all cues within it.</p>
        </ModalWrapper>
      )}
    </div>
  )
}

CueItemSettings.propTypes = {
  cue: PropTypes.object.isRequired,
  readonly: PropTypes.bool,
  onUpdateCue: PropTypes.func.isRequired,
  onDeleteCue: PropTypes.func.isRequired,
  parentId: PropTypes.string,
  SettingsMenu: PropTypes.node,
}

export default memo(CueItemSettings)
